import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '@/store/useAppStore'
import { aiPlatforms } from '@/constants/platforms'
import { api, type PromptDetailResponse, type GeneratedPrompt } from '@/services/api'
import type { PromptCategory } from '@/types'
import { useCreditEstimate } from '@/hooks/useCreditEstimate'
import { CreditEstimateScenario } from '@/services/creditEstimateService'
import { validateAndNavigate, getCurrentBalance } from '@/utils/validateCreditBalance'

// Simple markdown renderer for AI responses
const MarkdownContent: React.FC<{ content: string }> = ({ content }) => {
  const html = useMemo(() => {
    let result = content
      // Escape HTML
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      // Headers
      .replace(/^### (.+)$/gm, '<h4>$1</h4>')
      .replace(/^## (.+)$/gm, '<h3>$1</h3>')
      .replace(/^# (.+)$/gm, '<h2>$1</h2>')
      // Bold and italic
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Code blocks
      .replace(/```[\w]*\n?([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
      .replace(/`([^`]+)`/g, '<code>$1</code>')
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
      // Lists
      .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
      // Line breaks
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br/>')

    // Wrap in paragraph
    result = '<p>' + result + '</p>'
    // Wrap consecutive li in ul
    result = result.replace(/(<li>.*?<\/li>)+/gs, (match) => `<ul>${match}</ul>`)
    // Handle tables
    result = result.replace(/\|(.+)\|/g, (match) => {
      const cells = match.split('|').filter(c => c.trim())
      if (cells.every(c => /^[-:]+$/.test(c.trim()))) return '' // separator row
      const tag = 'td'
      return '<tr>' + cells.map(c => `<${tag}>${c.trim()}</${tag}>`).join('') + '</tr>'
    })
    result = result.replace(/(<tr>.*<\/tr>)+/gs, (match) => `<table>${match}</table>`)
    return result
  }, [content])

  return <div className="markdown-content" dangerouslySetInnerHTML={{ __html: html }} />
}

// Check if a loading prompt has timed out (> 5 minutes)
const isLoadingTimeout = (createdAt?: string): boolean => {
  if (!createdAt) return false
  const created = new Date(createdAt).getTime()
  const now = Date.now()
  return now - created > 5 * 60 * 1000 // 5 minutes
}

// Pill type for selected prompts
interface Pill {
  key: string
  text: string
  fromSuggestion?: string // suggestion id if from suggestions
}

export const PromptsPage: React.FC = () => {
  const navigate = useNavigate()
  const [viewingPrompt, setViewingPrompt] = useState<string | null>(null)
  const [promptDetail, setPromptDetail] = useState<PromptDetailResponse | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [expandedCard, setExpandedCard] = useState<string | null>(null)
  const [refreshingPromptId, setRefreshingPromptId] = useState<string | null>(null)

  // Pills input state
  const [draftText, setDraftText] = useState('')
  const [selectedPills, setSelectedPills] = useState<Pill[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const inputContainerRef = useRef<HTMLDivElement>(null)

  // Suggestions state
  const [generatedPrompts, setGeneratedPrompts] = useState<GeneratedPrompt[]>([])
  const [isSuggestLoading, setIsSuggestLoading] = useState(false)
  const [suggestCount, setSuggestCount] = useState(5)
  const [isTracking, setIsTracking] = useState(false)
  const hasFetchedSuggestions = useRef(false)

  const prompts = useAppStore((state) => state.prompts)
  const fetchPrompts = useAppStore((state) => state.fetchPrompts)
  const createPrompt = useAppStore((state) => state.createPrompt)
  const deletePrompt = useAppStore((state) => state.deletePrompt)
  const selectedPromptIds = useAppStore((state) => state.selectedPromptIds)
  const togglePromptSelection = useAppStore((state) => state.togglePromptSelection)
  const clearPromptSelection = useAppStore((state) => state.clearPromptSelection)

  const [isDeletingSelected, setIsDeletingSelected] = useState(false)

  // Prompt generation estimate (for Refresh/More buttons)
  const { formattedEstimate: generateEstimate, requiredMinimumBalance: generateRequired } = useCreditEstimate({
    enabled: showSuggestions,
    scenario: CreditEstimateScenario.PROMPT_GENERATION,
    params: { count: suggestCount },
    debounceMs: 250,
  })

  // Prompt tracking estimate (for Track button)
  // selectedPills 是已选中的prompt数量
  const pillCount = selectedPills.length + (draftText.trim() ? draftText.split(/[,，]/).filter(s => s.trim()).length : 0)
  const { formattedEstimate: trackEstimate, requiredMinimumBalance: trackRequired } = useCreditEstimate({
    enabled: pillCount > 0,
    scenario: CreditEstimateScenario.PROMPT_TRACKING,
    params: { count: pillCount },
    debounceMs: 250,
  })

  useEffect(() => {
    fetchPrompts()
  }, [fetchPrompts])

  // Close suggestions only when clicking outside the input area/dropdown.
  // This keeps the dropdown open during multi-select interactions.
  useEffect(() => {
    if (!showSuggestions) return

    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null
      if (!target) return
      const el = inputContainerRef.current
      if (el && !el.contains(target)) {
        setShowSuggestions(false)
      }
    }

    window.addEventListener('pointerdown', onPointerDown)
    return () => window.removeEventListener('pointerdown', onPointerDown)
  }, [showSuggestions])

  // Poll for updates when any prompt is loading
  const pollingRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isPollingRef = useRef(false)

  useEffect(() => {
    const hasWaiting = prompts.some((p) => {
      const results = Array.isArray((p as any).results) ? (p as any).results : []
      const anyPendingOrRunning = results.some((r: any) => r?.status === 'pending' || r?.status === 'running')
      // Fallback: just-created prompt may not have results rows yet, but is still waiting
      const noResultsYetButLoading = Boolean((p as any).loading) && results.length === 0
      return anyPendingOrRunning || noResultsYetButLoading
    })

    // Define the polling function
    const poll = async () => {
      if (!isPollingRef.current) return
      try {
        await fetchPrompts()
      } catch (err) {
        console.error('Polling fetchPrompts failed:', err)
      }
      // Schedule next poll if still needed
      if (isPollingRef.current) {
        pollingRef.current = setTimeout(poll, 5000)
      }
    }

    if (hasWaiting && !isPollingRef.current) {
      // Start polling
      isPollingRef.current = true
      pollingRef.current = setTimeout(poll, 5000)
    } else if (!hasWaiting && isPollingRef.current) {
      // Stop polling
      isPollingRef.current = false
      if (pollingRef.current) {
        clearTimeout(pollingRef.current)
        pollingRef.current = null
      }
    }

    // Cleanup on unmount
    return () => {
      isPollingRef.current = false
      if (pollingRef.current) {
        clearTimeout(pollingRef.current)
        pollingRef.current = null
      }
    }
  }, [prompts, fetchPrompts])

  // Fetch detail when viewing a prompt
  const openPromptDetail = useCallback(async (promptId: string) => {
    setViewingPrompt(promptId)
    setPromptDetail(null)
    setDetailLoading(true)
    try {
      const response = await api.prompts.getDetail(promptId)
      if (response.success) {
        setPromptDetail(response)
      }
    } catch (error) {
      console.error('Failed to fetch prompt detail:', error)
    } finally {
      setDetailLoading(false)
    }
  }, [])

  // ==================== Pills & Suggestions Logic ====================

  // Commit draft text to pills (split by comma or Chinese comma)
  const commitDraftToPills = useCallback((text: string) => {
    const parts = text.split(/[,，]/).map(s => s.trim()).filter(s => s.length > 0)
    if (parts.length === 0) return

    setSelectedPills(prev => {
      const existingTexts = new Set(prev.map(p => p.text.toLowerCase()))
      const newPills: Pill[] = []
      for (const part of parts) {
        if (!existingTexts.has(part.toLowerCase())) {
          existingTexts.add(part.toLowerCase())
          newPills.push({
            key: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            text: part,
          })
        }
      }
      return [...prev, ...newPills]
    })
    setDraftText('')
  }, [])

  // Handle input keydown for comma or enter
  const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === ',' || e.key === 'Enter') {
      e.preventDefault()
      if (draftText.trim()) {
        commitDraftToPills(draftText)
      }
    } else if (e.key === 'Backspace' && draftText === '' && selectedPills.length > 0) {
      // Remove last pill on backspace when input is empty
      setSelectedPills(prev => prev.slice(0, -1))
    }
  }, [draftText, selectedPills, commitDraftToPills])

  // Remove a pill
  const removePill = useCallback((pillKey: string) => {
    setSelectedPills(prev => prev.filter(p => p.key !== pillKey))
  }, [])

  // Toggle suggestion selection
  const toggleSuggestion = useCallback((gp: GeneratedPrompt) => {
    setSelectedPills(prev => {
      const existing = prev.find(p => p.fromSuggestion === gp.id)
      if (existing) {
        // Deselect
        return prev.filter(p => p.fromSuggestion !== gp.id)
      } else {
        // Select - add as pill
        return [...prev, {
          key: `suggestion_${gp.id}`,
          text: gp.text,
          fromSuggestion: gp.id,
        }]
      }
    })
  }, [])

  // Check if a suggestion is selected
  const isSuggestionSelected = useCallback((gpId: string) => {
    return selectedPills.some(p => p.fromSuggestion === gpId)
  }, [selectedPills])

  // Check if all suggestions are selected
  const isAllSuggestionsSelected = useMemo(() => {
    if (generatedPrompts.length === 0) return false
    return generatedPrompts.every(gp => selectedPills.some(p => p.fromSuggestion === gp.id))
  }, [generatedPrompts, selectedPills])

  // Toggle select all suggestions
  const toggleSelectAllSuggestions = useCallback(() => {
    if (isAllSuggestionsSelected) {
      // Deselect all suggestions (keep custom pills)
      setSelectedPills(prev => prev.filter(p => !p.fromSuggestion))
    } else {
      // Select all suggestions
      setSelectedPills(prev => {
        const existingSuggestionIds = new Set(prev.filter(p => p.fromSuggestion).map(p => p.fromSuggestion))
        const newPills = generatedPrompts
          .filter(gp => !existingSuggestionIds.has(gp.id))
          .map(gp => ({
            key: `suggestion_${gp.id}`,
            text: gp.text,
            fromSuggestion: gp.id,
          }))
        return [...prev, ...newPills]
      })
    }
  }, [isAllSuggestionsSelected, generatedPrompts])

  // Ensure suggestions are loaded (on focus when draft is empty)
  const ensureSuggestions = useCallback(async () => {
    if (generatedPrompts.length > 0 || hasFetchedSuggestions.current) return // Already have suggestions or already fetched

    hasFetchedSuggestions.current = true
    setIsSuggestLoading(true)
    try {
      // First try to get cached
      const response = await api.prompts.getGenerated()
      if (response.success && response.data.length > 0) {
        setGeneratedPrompts(response.data)
      } else {
        // Generate fresh if no cache
        const genResponse = await api.prompts.generatePrompts({ count: 5, mode: 'fresh' })
        if (genResponse.success) {
          setGeneratedPrompts(genResponse.data)
        }
      }
    } catch (error) {
      console.error('Failed to load suggestions:', error)
    } finally {
      setIsSuggestLoading(false)
    }
  }, [generatedPrompts.length])

  // Refresh suggestions (fresh mode)
  const handleRefreshSuggestions = useCallback(async () => {
    // 余额校验
    const currentBalance = getCurrentBalance()
    if (generateRequired && !validateAndNavigate(generateRequired, currentBalance, navigate)) {
      return // Insufficient balance, modal shown
    }

    setIsSuggestLoading(true)
    try {
      const response = await api.prompts.generatePrompts({ count: 5, mode: 'fresh' })
      if (response.success) {
        setGeneratedPrompts(response.data)
      }
    } catch (error) {
      console.error('Failed to refresh suggestions:', error)
    } finally {
      setIsSuggestLoading(false)
    }
  }, [generateRequired, navigate])

  // Generate more suggestions
  const handleMoreSuggestions = useCallback(async () => {
    // 余额校验
    const currentBalance = getCurrentBalance()
    if (generateRequired && !validateAndNavigate(generateRequired, currentBalance, navigate)) {
      return // Insufficient balance, modal shown
    }

    setIsSuggestLoading(true)
    try {
      const response = await api.prompts.generatePrompts({ count: suggestCount, mode: 'more' })
      if (response.success) {
        setGeneratedPrompts(response.data)
      }
    } catch (error) {
      console.error('Failed to generate more suggestions:', error)
    } finally {
      setIsSuggestLoading(false)
    }
  }, [suggestCount, generateRequired, navigate])

  // Handle focus - show suggestions if draft is empty
  const handleInputFocus = useCallback(() => {
    setIsFocused(true)
    if (draftText.trim() === '') {
      setShowSuggestions(true)
      ensureSuggestions()
    }
  }, [draftText, ensureSuggestions])

  // Handle blur - do NOT auto-close suggestions (click-outside handles closing)
  const handleInputBlur = useCallback(() => {
    setIsFocused(false)
  }, [])

  // Handle input change
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setDraftText(value)
    // Show suggestions only when input is empty
    if (value.trim() === '' && isFocused) {
      setShowSuggestions(true)
      ensureSuggestions()
    } else {
      setShowSuggestions(false)
    }
  }, [isFocused, ensureSuggestions])

  // Handle Track button - batch create all pills
  const handleTrack = useCallback(async () => {
    // First commit any remaining draft
    let finalPills = [...selectedPills]
    if (draftText.trim()) {
      const parts = draftText.split(/[,，]/).map(s => s.trim()).filter(s => s.length > 0)
      const existingTexts = new Set(finalPills.map(p => p.text.toLowerCase()))
      for (const part of parts) {
        if (!existingTexts.has(part.toLowerCase())) {
          existingTexts.add(part.toLowerCase())
          finalPills.push({
            key: `custom_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            text: part,
          })
        }
      }
    }

    if (finalPills.length === 0) return

    // 余额校验
    const currentBalance = getCurrentBalance()
    if (trackRequired && !validateAndNavigate(trackRequired, currentBalance, navigate)) {
      return // Insufficient balance, modal shown
    }

    setIsTracking(true)
    try {
      const succeededSuggestionIds: string[] = []

      for (const pill of finalPills) {
        try {
          const category: PromptCategory = 'blog'
          await createPrompt(pill.text, category)

          // If this was from a suggestion, delete it from cache
          if (pill.fromSuggestion) {
            try {
              await api.prompts.deleteGenerated(pill.fromSuggestion)
              succeededSuggestionIds.push(pill.fromSuggestion)
            } catch {
              // Ignore delete errors
            }
          }
        } catch (err) {
          console.error('Failed to create prompt:', err)
        }
      }

      // Clear pills and draft
      setSelectedPills([])
      setDraftText('')
      setShowSuggestions(false)

      // Remove tracked suggestions from list
      if (succeededSuggestionIds.length > 0) {
        setGeneratedPrompts(prev => prev.filter(gp => !succeededSuggestionIds.includes(gp.id)))
      }

      // Refresh prompts list
      await fetchPrompts()
    } catch (error) {
      console.error('Failed to track prompts:', error)
    } finally {
      setIsTracking(false)
    }
  }, [selectedPills, draftText, createPrompt, fetchPrompts, trackRequired, navigate])

  // Handle refresh/re-track a prompt
  const handleRefresh = useCallback(async (promptId: string) => {
    setRefreshingPromptId(promptId)
    try {
      const response = await api.prompts.track(promptId)
      if (response.success) {
        // Refresh the list to show updated loading state
        await fetchPrompts()
      }
    } catch (error) {
      console.error('Failed to refresh prompt:', error)
    } finally {
      setRefreshingPromptId(null)
    }
  }, [fetchPrompts])

  // Platform order: ChatGPT, Claude, Gemini, Perplexity (Perplexity before Citations)
  const displayPlatforms = ['chatgpt', 'claude', 'gemini', 'perplexity']

  // Reset expanded card when opening new detail
  useEffect(() => {
    if (viewingPrompt && promptDetail) {
      // Default first card expanded
      const firstPlatform = aiPlatforms[0]?.id
      if (firstPlatform) {
        setExpandedCard(firstPlatform)
      }
    }
  }, [viewingPrompt, promptDetail])

  // Get platform info by id
  const getPlatformInfo = (id: string) => aiPlatforms.find(p => p.id === id)

  const isAllTrackedSelected = useMemo(() => {
    if (prompts.length === 0) return false
    const selected = new Set(selectedPromptIds)
    return prompts.every((p) => selected.has(p.id))
  }, [prompts, selectedPromptIds])

  const toggleSelectAllTracked = useCallback(() => {
    if (isAllTrackedSelected) {
      clearPromptSelection()
      return
    }
    clearPromptSelection()
    // select all currently visible prompts
    for (const p of prompts) {
      togglePromptSelection(p.id)
    }
  }, [isAllTrackedSelected, clearPromptSelection, togglePromptSelection, prompts])

  const deleteSelectedTracked = useCallback(async () => {
    if (selectedPromptIds.length === 0) return
    const ids = [...selectedPromptIds]
    setIsDeletingSelected(true)
    try {
      for (const id of ids) {
        await deletePrompt(id)
      }
      // ensure list stays in sync (esp. if pagination/offset later)
      await fetchPrompts()
      clearPromptSelection()
    } catch (err) {
      console.error('Failed to delete selected prompts:', err)
    } finally {
      setIsDeletingSelected(false)
    }
  }, [selectedPromptIds, deletePrompt, fetchPrompts, clearPromptSelection])

  // Create content from a single prompt row
  const handleCreateFromSingle = useCallback((promptId: string) => {
    clearPromptSelection()
    togglePromptSelection(promptId)
    navigate('/app/content')
  }, [clearPromptSelection, togglePromptSelection, navigate])

  // Create content from selected prompts (batch)
  const handleBatchCreate = useCallback(() => {
    navigate('/app/content')
  }, [navigate])

  return (
    <>
      <div className="main-header">
        <h1 className="main-title">Prompt Tracking</h1>
      </div>
      <div className="main-body">
        {/* Track Input with Pills and Suggestions Dropdown */}
        <div className="prompt-input-container" ref={inputContainerRef}>
          <div className="prompt-input-wrap">
            <div className="prompt-pills-input" onClick={() => inputRef.current?.focus()}>
              {selectedPills.map((pill) => (
                <div key={pill.key} className="prompt-pill" title={pill.text}>
                  <span className="prompt-pill__text">{pill.text}</span>
                  <button
                    className="prompt-pill__remove"
                    onClick={(e) => {
                      e.stopPropagation()
                      removePill(pill.key)
                    }}
                    type="button"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              ))}
              <input
                ref={inputRef}
                type="text"
                className="prompt-pills-input__field"
                placeholder={selectedPills.length === 0 ? "Enter prompts to track, e.g. best CRM software for startups" : ""}
                value={draftText}
                onChange={handleInputChange}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
              />
            </div>
            <button
              className="btn btn-primary"
              onClick={handleTrack}
              disabled={isTracking || (selectedPills.length === 0 && draftText.trim() === '')}
            >
              {isTracking ? 'Tracking...' : 'Track'}
            </button>
          </div>

          {/* Suggestions Dropdown */}
          {showSuggestions && (
            <div className="prompt-suggestions" id="suggested-section">
              <div className="prompt-suggestions-header">
                <span className="prompt-suggestions-label">
                  {generatedPrompts.length > 0 && (
                    <input
                      type="checkbox"
                      checked={isAllSuggestionsSelected}
                      onChange={(e) => {
                        e.stopPropagation()
                        toggleSelectAllSuggestions()
                      }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ marginRight: '8px', cursor: 'pointer' }}
                      aria-label="Select all suggestions"
                    />
                  )}
                  <svg className="icon" style={{ width: '14px', height: '14px' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                  </svg>
                  Suggestions
                </span>
                {generateEstimate && (
                  <span className="credit-estimate-hint">~{generateEstimate} credits</span>
                )}
                <button
                  className="btn-text-sm"
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleRefreshSuggestions()
                  }}
                  disabled={isSuggestLoading}
                >
                  {isSuggestLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>
              <div className="prompt-suggestions-list">
                {isSuggestLoading && generatedPrompts.length === 0 ? (
                  <div className="prompt-suggestions-loading">
                    <div className="analysis-spinner" style={{ width: '16px', height: '16px', margin: '0 auto' }}></div>
                  </div>
                ) : generatedPrompts.length === 0 ? (
                  <div className="prompt-suggestions-empty">
                    No suggestions available. Click Refresh to generate.
                  </div>
                ) : (
                  generatedPrompts.map((gp) => (
                    <div
                      key={gp.id}
                      className={`prompt-suggestion-item ${isSuggestionSelected(gp.id) ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        toggleSuggestion(gp)
                      }}
                    >
                      <div className="prompt-suggestion-checkbox">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      </div>
                      <span className="prompt-suggestion-text">{gp.text}</span>
                    </div>
                  ))
                )}
              </div>
              {generatedPrompts.length > 0 && (
                <div className="prompt-suggestions-footer">
                  <div className="prompt-suggestions-footer-left">
                    <label style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Count:</label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={suggestCount}
                      onChange={(e) => setSuggestCount(Math.max(1, Math.min(10, parseInt(e.target.value) || 5)))}
                      className="prompt-suggestions-count-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div className="prompt-suggestions-footer-right">
                    {generateEstimate && (
                      <span className="credit-estimate-hint">~{generateEstimate} credits</span>
                    )}
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleMoreSuggestions()
                      }}
                      disabled={isSuggestLoading}
                    >
                      More
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tracked Prompts List */}
        <div className="tracked-section">
          <div className="tracked-header">
            <span className="tracked-label">Tracked Prompts</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              {selectedPromptIds.length > 0 && (
                <span className="tracked-count">
                  {selectedPromptIds.length} selected
                </span>
              )}
              <span className="tracked-count">{prompts.length} prompts</span>
              {selectedPromptIds.length > 0 && (
                <>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleBatchCreate}
                    title="Create content from selected prompts"
                    style={{ backgroundColor: '#000', borderColor: '#000' }}
                  >
                    Create
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={deleteSelectedTracked}
                    disabled={isDeletingSelected}
                    title="Delete selected prompts"
                  >
                    {isDeletingSelected ? 'Deleting...' : 'Delete'}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Floating estimate hint - only show when there are prompts to track */}
          {pillCount > 0 && trackEstimate && (
            <div className="track-estimate-hint">
              Estimated: {trackEstimate} credits ({pillCount} prompt{pillCount > 1 ? 's' : ''})
            </div>
          )}
          {prompts.length === 0 ? (
            <div className="empty-state" id="prompt-empty">
              <h3>No prompts tracked yet</h3>
              <p>Track prompts to monitor your visibility across AI platforms</p>
            </div>
          ) : (
            <div className="prompt-list" id="prompt-list">
              <div className="prompt-table">
                <div className="prompt-table-header">
                  <div className="header-select" style={{ display: 'flex', justifyContent: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isAllTrackedSelected}
                      onChange={toggleSelectAllTracked}
                      aria-label="Select all tracked prompts"
                    />
                  </div>
                  <div className="header-prompt">Prompt</div>
                  {displayPlatforms.map((platformId) => {
                    const platform = getPlatformInfo(platformId)
                    return (
                      <div key={platformId} className="header-platform" title={platform?.name}>
                        <div
                          className={`header-platform-icon ${platformId}`}
                          dangerouslySetInnerHTML={{ __html: platform?.icon || '' }}
                        />
                      </div>
                    )
                  })}
                  <div className="header-citations">Citations</div>
                  <div></div>
                </div>
                {prompts.map((p) => {
                  const results = Array.isArray(p.results) ? p.results : []
                  const hasWaiting =
                    results.some((r: any) => r?.status === 'pending' || r?.status === 'running') ||
                    // Fallback: just-created prompt may not have results rows yet, but is still waiting
                    (Boolean(p.loading) && results.length === 0)
                  const hasFailed = results.some((r: any) => r?.status === 'failed')
                  const timedOut = hasWaiting && isLoadingTimeout(p.created_at)
                  const isRefreshing = refreshingPromptId === p.id

                  return (
                    <div
                      key={p.id}
                      className="prompt-row"
                      style={{ opacity: hasWaiting ? 0.6 : 1 }}
                      onClick={() => openPromptDetail(p.id)}
                    >
                      <div
                        className="select-cell"
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: 'flex', justifyContent: 'center' }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedPromptIds.includes(p.id)}
                          onChange={() => togglePromptSelection(p.id)}
                          aria-label={`Select prompt ${p.text}`}
                        />
                      </div>
                      <div className="prompt-row-text" title={p.text} style={hasWaiting ? { color: 'var(--text-muted)' } : undefined}>
                        {p.text}
                      </div>
                      {displayPlatforms.map((platformId) => {
                        const result = p.results?.find(r => r.platformId === platformId)
                        if (!result) {
                          return (
                            <div key={platformId} className="platform-cell">
                              <span className="status-text pending">—</span>
                            </div>
                          )
                        }

                        if (result.status === 'failed') {
                          return (
                            <div key={platformId} className="platform-cell">
                              <span className="status-text failed">Error</span>
                            </div>
                          )
                        }

                        if (result.status === 'pending' || result.status === 'running') {
                          return (
                            <div key={platformId} className="platform-cell">
                              <div className="analysis-spinner" style={{ width: '14px', height: '14px', margin: 0 }}></div>
                            </div>
                          )
                        }

                        const statusClass = result.mentioned
                          ? (result.sentiment || 'neutral')
                          : 'not-mentioned'
                        const statusText = result.mentioned
                          ? (result.sentiment === 'positive' ? 'Positive' : result.sentiment === 'negative' ? 'Negative' : 'Neutral')
                          : 'Not Mentioned'

                        return (
                          <div key={platformId} className="platform-cell">
                            <span className={`status-text ${statusClass}`}>{statusText}</span>
                          </div>
                        )
                      })}
                      <div className="citation-cell">
                        {(() => {
                          // Get platforms with brand citations > 0
                          const platformsWithBrandCitations = displayPlatforms
                            .map(platformId => {
                              const result = results.find((r: any) => r.platformId === platformId)
                              return {
                                platformId,
                                count: result?.brandCitationCount || 0
                              }
                            })
                            .filter(item => item.count > 0)

                          if (platformsWithBrandCitations.length === 0) {
                            return <span className="citation-empty">—</span>
                          }

                          return (
                            <div className="citation-platform-list">
                              {platformsWithBrandCitations.map(item => {
                                const platform = getPlatformInfo(item.platformId)
                                return (
                                  <div key={item.platformId} className="citation-platform-item">
                                    <div
                                      className={`citation-platform-icon ${item.platformId}`}
                                      dangerouslySetInnerHTML={{ __html: platform?.icon || '' }}
                                    />
                                    <span className="citation-platform-count">{item.count}</span>
                                  </div>
                                )
                              })}
                            </div>
                          )
                        })()}
                      </div>
                      <div className="action-cell" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleCreateFromSingle(p.id)}
                          title="Create content from this prompt"
                          style={{ backgroundColor: '#000', borderColor: '#000' }}
                        >
                          Create
                        </button>
                        <button
                          className="btn btn-secondary btn-sm"
                          disabled={isRefreshing || (hasWaiting && !timedOut && !hasFailed)}
                          onClick={() => handleRefresh(p.id)}
                        >
                          {refreshingPromptId === p.id ? '...' : 'Refresh'}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Prompt Detail Modal */}
      {viewingPrompt !== null && (
        <div className={`prompt-detail-modal ${viewingPrompt !== null ? 'show' : ''}`} onClick={() => { setViewingPrompt(null); setPromptDetail(null); }}>
          <div className="prompt-detail-content" onClick={(e) => e.stopPropagation()}>
            {detailLoading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <div className="analysis-spinner" style={{ width: '24px', height: '24px', margin: '0 auto 16px' }}></div>
                <p style={{ color: 'var(--text-muted)' }}>Loading details...</p>
              </div>
            ) : promptDetail ? (
              <>
                <div className="prompt-detail-header">
                  <div className="prompt-detail-title">{promptDetail.text}</div>
                  <button className="prompt-detail-close" onClick={() => { setViewingPrompt(null); setPromptDetail(null); }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                <div className="prompt-detail-body">
                  {aiPlatforms.map((pl) => {
                    // Find platform detail from API response
                    const platformData = promptDetail.platforms.find(p => p.platform_id === pl.id)
                    const mentioned = platformData?.mentioned ?? false
                    const sentiment = platformData?.sentiment
                    const rawAnswerText = platformData?.raw_answer_text
                    const citations = platformData?.citations ?? []
                    const status = platformData?.status

                    // Show loading state if platform is still running
                    const isRunning = status === 'pending' || status === 'running'
                    const isExpanded = expandedCard === pl.id

                    const handleCardClick = () => {
                      setExpandedCard(isExpanded ? null : pl.id)
                    }

                    return (
                      <div key={pl.id} className={`response-card ${isExpanded ? 'expanded' : 'collapsed'}`}>
                        <div className="response-card-header" onClick={handleCardClick}>
                          <div className="response-platform">
                            <div
                              className={`response-platform-icon ${pl.id}`}
                              dangerouslySetInnerHTML={{ __html: (pl as any)?.icon || '' }}
                            ></div>
                            <span className="response-platform-name">{pl.name}</span>
                          </div>
                          <div className="response-status">
                            {isRunning ? (
                              <span
                                className="response-status-tag"
                                style={{ background: 'var(--bg)', color: 'var(--text-muted)' }}
                              >
                                Processing...
                              </span>
                            ) : platformData ? (
                              <>
                                <span className={`response-status-tag ${mentioned ? 'mentioned' : 'not-mentioned'}`}>
                                  {mentioned ? 'Mentioned' : 'Not Mentioned'}
                                </span>
                                {mentioned && sentiment && (
                                  <span className={`response-status-tag ${sentiment}`}>
                                    {sentiment.charAt(0).toUpperCase() + sentiment.slice(1)}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="response-status-tag not-mentioned">—</span>
                            )}
                            <svg
                              className={`accordion-arrow ${isExpanded ? 'expanded' : ''}`}
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <polyline points="6,9 12,15 18,9" />
                            </svg>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="response-card-body">
                            {isRunning ? (
                              <div
                                className="response-not-mentioned"
                                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                              >
                                <div className="analysis-spinner" style={{ width: '14px', height: '14px', margin: 0 }}></div>
                                Analyzing response...
                              </div>
                            ) : rawAnswerText ? (
                              <div className="response-text-container">
                                <MarkdownContent content={rawAnswerText} />
                              </div>
                            ) : mentioned ? (
                              <div className="response-text">Response data available.</div>
                            ) : (
                              <div className="response-not-mentioned">Your brand was not mentioned in this AI's response.</div>
                            )}
                            {citations.length > 0 && (
                              <div className="response-citations">
                                <div className="response-citations-title">Citations ({citations.length})</div>
                                <div className="response-citations-list">
                                  {citations.map((c, idx) => (
                                    <div key={idx} className={`citation-item ${c.brand_cited ? 'brand-cited' : ''}`}>
                                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                      </svg>
                                      <a href={c.url} target="_blank" rel="noopener noreferrer">
                                        {c.title || c.url}
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className="prompt-detail-footer">
                  <button className="btn btn-secondary" onClick={() => { setViewingPrompt(null); setPromptDetail(null); }}>Close</button>
                  <button className="btn btn-primary" onClick={() => { setViewingPrompt(null); setPromptDetail(null); navigate('/app/content'); }}>
                    Create Content
                  </button>
                </div>
              </>
            ) : (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <p style={{ color: 'var(--text-muted)' }}>Failed to load details</p>
                <button className="btn btn-secondary" onClick={() => { setViewingPrompt(null); setPromptDetail(null); }} style={{ marginTop: '16px' }}>
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}
